/**
 * Reverse declension: given an inflected surface form, work out which stems it
 * could come from.
 *
 * The old implementation inverted its sandhi rules and returned everything the
 * inversion allowed, which produced around nineteen candidates per lookup —
 * including non-words — because rule inversion is inherently ambiguous and
 * nothing checked the results.
 *
 * This works the other way round: guess cheaply, then verify by *re-deriving*.
 * A candidate is only returned if the grammar engine, asked to decline that stem
 * in that case and number, actually produces the form we started from. Nothing
 * is returned that the engine cannot regenerate.
 */

import { CaseName, Gender, Num } from "./types";
import { annotate, LexicalHints } from "./annotation";
import { deriveCell, normalizeIast } from "./vidyut-engine";
import type { VerbAnalysis } from "./verb-lookup";
import endingsData from "./data/endings.json";

/**
 * One entry of the derived ending table: "a form ending in `formSuffix` may come
 * from a stem ending in `stemSuffix`, in this cell".
 */
export type EndingRule = {
    formSuffix: string;
    stemSuffix: string;
    case: CaseName;
    num: Num;
    gender: Gender;
    stemClass: string;
};

const ENDINGS = endingsData as EndingRule[];

/** A stem the surface form could have come from, with the cell it would be. */
export type Analysis = {
    /** The reconstructed stem. */
    word: string;
    gen: Gender;
    case: CaseName;
    num: Num;
    /** Which paradigm matched, e.g. "an-stem". */
    stemClass: string;
};

/** Kept for compatibility with the previous API shape. */
export type DeclensionWord = {
    word: string;
    gen: string;
    case: string;
    num: string;
};

/** Reconstructs the candidate stems a surface form could be built on. */
const candidateStems = (surface: string): Array<{ stem: string; rule: EndingRule }> => {
    const out: Array<{ stem: string; rule: EndingRule }> = [];
    const seen = new Set<string>();

    for (const rule of ENDINGS) {
        if (rule.formSuffix.length > 0 && !surface.endsWith(rule.formSuffix)) continue;
        const base = rule.formSuffix.length === 0
            ? surface
            : surface.slice(0, surface.length - rule.formSuffix.length);
        if (base.length === 0) continue;

        const stem = base + rule.stemSuffix;
        const key = `${stem}|${rule.case}|${rule.num}|${rule.gender}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ stem, rule });
    }
    return out;
};

/**
 * Analyses a surface form.
 *
 * `hints` may carry what the dictionary knows about the word; without it the
 * annotation falls back to shape, which is right for the regular majority.
 * `limit` bounds how many candidates get verified, since each verification is a
 * derivation.
 */
export const analyze = async (
    surface: string,
    hints: LexicalHints = {},
    limit = 400,
): Promise<Analysis[]> => {
    const target = normalizeIast(surface.trim().toLowerCase());
    if (!target) return [];

    const candidates = candidateStems(target).slice(0, limit);
    const confirmed: Analysis[] = [];
    const emitted = new Set<string>();

    // Cache derivations: many candidates share a (stem, gender, case, num).
    const cache = new Map<string, Set<string>>();

    for (const { stem, rule } of candidates) {
        const key = `${stem}|${rule.gender}|${rule.case}|${rule.num}`;
        let forms = cache.get(key);

        if (!forms) {
            const { analysis } = annotate(stem, rule.gender, hints);
            const derived = await deriveCell(analysis, rule.gender, rule.case, rule.num);
            forms = new Set(derived.map(d => normalizeIast(d.text)));
            cache.set(key, forms);
        }

        // The verification step: keep it only if the grammar really produces it.
        if (!forms.has(target)) continue;

        const outKey = `${stem}|${rule.gender}|${rule.case}|${rule.num}`;
        if (emitted.has(outKey)) continue;
        emitted.add(outKey);

        confirmed.push({
            word: stem,
            gen: rule.gender,
            case: rule.case,
            num: rule.num,
            stemClass: rule.stemClass,
        });
    }

    return confirmed;
};

/**
 * Backwards-compatible wrapper matching the shape the dictionary search expects.
 * Now asynchronous, because the grammar engine loads on demand; the one caller
 * already runs inside an async function.
 */
export const reverseDeclension = async (word: string): Promise<DeclensionWord[]> => {
    const results = await analyze(word);
    return results.map(r => ({ word: r.word, gen: r.gen, case: r.case, num: r.num }));
};

/**
 * Analyses a form as a finite verb.
 *
 * Kept separate from the nominal path, and separately loaded: the index is
 * 743 KB over the wire, so it is fetched only when a form does not resolve as a
 * noun — which is the great majority of lookups.
 */
export const analyzeVerb = async (surface: string): Promise<VerbAnalysis[]> => {
    const { loadVerbForms, lookupVerb } = await import("./verb-lookup");
    await loadVerbForms();
    return lookupVerb(normalizeIast(surface.trim().toLowerCase()));
};
